import { toMetricSelection, type SemanticQueryInput } from "@cd/core";
import type { CatalogDimension, PackCatalog } from "../../lib/api";
import { drillBy, drillFilters, narrowedRefs, type Drill } from "../../lib/drill";
import { fieldList, type FieldSection } from "../builder/fieldList";
import { emptyDraft, type Draft } from "../builder/store";

const NARROWED = "Already narrowed to one value";

/**
 * The fields a breakdown can be split by: the builder's list for the widget's metrics and filters,
 * so fields that would double-count or come from another pack collapse with the same reasons. A
 * field already narrowed to one value (by a step, or by the widget's own filter) would split into a
 * single bar, so it collapses too.
 */
export function breakdownFields(packs: PackCatalog[], source: SemanticQueryInput, drill: Drill, q = ""): FieldSection<CatalogDimension> {
  const by = drillBy(drill);
  const draft: Draft = {
    ...emptyDraft(),
    pack: source.pack,
    metrics: source.metrics.map(toMetricSelection),
    dimensions: by ? [by] : [],
    filters: [...(source.filters ?? []).map((f) => ({ ...f, values: f.values ?? [] })), ...drillFilters(drill)],
  };
  const { dimensions } = fieldList(packs, draft, q);
  const pinned = new Set((source.filters ?? [])
    .filter((f) => f.op === "eq" || f.op === "is_null" || (f.op === "in" && f.values?.length === 1)).map((f) => f.ref));
  const narrowed = new Set([...narrowedRefs(drill), ...pinned]);
  const isNarrowed = (ref: string) => narrowed.has(ref) && ref !== by?.ref;

  const moved = dimensions.available.flatMap((p) => p.models.flatMap(([, rows]) => rows.filter((r) => isNarrowed(r.field.ref))));
  if (!moved.length) return dimensions;
  return {
    available: dimensions.available
      .map((p) => ({ ...p, models: p.models.map(([m, rows]) => [m, rows.filter((r) => !isNarrowed(r.field.ref))] as typeof p.models[number]).filter(([, rows]) => rows.length) }))
      .filter((p) => p.models.length),
    blocked: [
      [NARROWED, moved.map((r) => ({ ...r, blocker: { group: NARROWED, detail: `This breakdown is already narrowed to one ${r.field.label.toLowerCase()}.` } }))],
      ...dimensions.blocked,
    ],
  };
}
